import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Language, TranslationService } from './translation.service';

/**
 * Interface for cached translation items
 */
interface CachedTranslation {
  translations: Record<string, string>;
  timestamp: number;
}

/**
 * Service that extends TranslationService with localStorage persistence
 */
@Injectable({
  providedIn: 'root',
})
export class PersistentTranslationService extends TranslationService {
  // Cache expiration time in milliseconds (default: 7 days)
  private cacheExpirationTime = 7 * 24 * 60 * 60 * 1000;

  constructor(http: HttpClient) {
    super(http);
    console.log('PersistentTranslationService initialized');
    this.loadCachedTranslations();
  }

  /**
   * Load all cached translations from localStorage on service initialization
   */
  private loadCachedTranslations(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const cacheKeys = Object.keys(localStorage).filter((key) =>
        key.startsWith('translations_')
      );

      if (cacheKeys.length > 0) {
        console.log(
          `Found ${cacheKeys.length} cached translations in localStorage`
        );
      }

      cacheKeys.forEach((key) => {
        try {
          const parts = key.split('_');
          if (parts.length >= 3) {
            const language = parts[1] as Language;
            const category = parts.slice(2).join('_');

            const cachedItemJson = localStorage.getItem(key);
            if (cachedItemJson) {
              const cachedItem = JSON.parse(
                cachedItemJson
              ) as CachedTranslation;

              if (
                Date.now() - cachedItem.timestamp >
                this.cacheExpirationTime
              ) {
                console.log(
                  `Cache for ${category} (${language}) has expired, removing`
                );
                localStorage.removeItem(key);
              } else {
                console.log(
                  `Loading cached translation for ${category} (${language})`
                );
                Object.entries(cachedItem.translations).forEach(
                  ([key, value]) => {
                    this.mergeTranslations({ [key]: value }, '');
                  }
                );
              }
            }
          }
        } catch (error) {
          console.warn(`Error processing cached translation ${key}:`, error);
        }
      });
    } catch (error) {
      console.warn('Error loading cached translations:', error);
    }
  }

  /**
   * Override the loadCategoryTranslations method to add localStorage caching
   */
  public override loadCategoryTranslations(
    language: Language,
    category: string
  ): Observable<void> {
    const cachedTranslation = this.getFromLocalStorageCache(language, category);
    if (cachedTranslation) {
      console.log(
        `🔄 LOCALSTORAGE CACHE HIT: Using cached ${category} translations for ${language}`
      );
      Object.entries(cachedTranslation.translations).forEach(([key, value]) => {
        this.mergeTranslations({ [key]: value }, '');
      });
      return of(undefined);
    }

    return super.loadCategoryTranslations(language, category).pipe(
      tap(() => {
        this.saveToLocalStorageCache(language, category);
      }),
      catchError((error) => {
        console.warn(`Error in persistent translation service:`, error);
        return of(undefined);
      })
    );
  }

  /**
   * Save translations to localStorage cache
   */
  private saveToLocalStorageCache(language: Language, category: string): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const translations = this.extractCategoryTranslations(category);
      if (!translations || Object.keys(translations).length === 0) return;

      const cacheKey = `translations_${language}_${category}`;
      const cacheItem: CachedTranslation = {
        translations,
        timestamp: Date.now(),
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
      console.log(
        `🔄 Saved ${category} translations for ${language} to localStorage cache`
      );
    } catch (error) {
      console.warn('Error saving to localStorage cache:', error);
    }
  }

  /**
   * Get translations from localStorage cache
   */
  private getFromLocalStorageCache(
    language: Language,
    category: string
  ): CachedTranslation | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const cacheKey = `translations_${language}_${category}`;
      const cacheItemJson = localStorage.getItem(cacheKey);

      if (!cacheItemJson) return null;

      const cacheItem = JSON.parse(cacheItemJson) as CachedTranslation;

      if (Date.now() - cacheItem.timestamp > this.cacheExpirationTime) {
        console.log(
          `🔄 Cache for ${category} (${language}) has expired, removing`
        );
        localStorage.removeItem(cacheKey);
        return null;
      }

      return cacheItem;
    } catch (error) {
      console.warn('Error reading from localStorage cache:', error);
      return null;
    }
  }

  /**
   * Helper method to extract translations for a specific category
   */
  private extractCategoryTranslations(
    category: string
  ): Record<string, string> {
    const translations: Record<string, string> = {};
    const prefix = `productDetails.${category}`;

    // Get all translations that start with the category prefix
    for (const [key, value] of this.getTranslations()) {
      if (key.startsWith(prefix)) {
        translations[key] = value;
      }
    }

    return translations;
  }
}
